// frontend/src/state/deriveDefaultEncodedKpi.ts
import type { KpiMetadataItem } from '~/api/endpoints/getControls'

/** Mirrors useKpiCascadeState.axisCount: a fact is 3-axis iff any item carries BOTH a driver
 *  and a value_type; otherwise it is a bare 1-axis fact. */
function hasFullAxes(items: KpiMetadataItem[]): boolean {
  return items.some((m) => m.driver !== null && m.value_type !== null)
}

/** Encode a chosen `kpi_root` into the SAME string the primary cascade would produce by default:
 *  - 1-axis fact → the bare root
 *  - 3-axis fact → `${root}__${driver0}__${vt0}` where driver0 is the first driver under the root
 *    and vt0 is the first value_type under (root, driver0) — matching the cascade's
 *    driverOptions[0] / valueTypeOptions[0] defaulting (useKpiCascadeState.ts:97-129).
 *  Returns the bare root if the root has no driver/value_type rows (loud-safe). */
export function deriveDefaultEncodedKpi(root: string, items: KpiMetadataItem[]): string {
  if (!hasFullAxes(items)) return root
  const driver0 = items.find((m) => m.kpi_root === root && m.driver !== null)?.driver
  if (driver0 == null) return root
  const vt0 = items.find(
    (m) => m.kpi_root === root && m.driver === driver0 && m.value_type !== null,
  )?.value_type
  if (vt0 == null) return root
  return `${root}__${driver0}__${vt0}`
}
